import { createAsyncThunk } from "@reduxjs/toolkit";
import AuthService from "../../infrastructure/api/auth";
import DefaultApiService from "../../infrastructure/api/default";
import UserManagementService from "../../infrastructure/api/userManagement";
import { normalizeAuthUser, readStoredUser } from "../../infrastructure/utils/authSession";
import {
  clearImpersonationOriginal,
  readImpersonationOriginal,
  saveImpersonationOriginal,
} from "../../infrastructure/utils/impersonationSession";
import { setMessage } from "../slices/message";
import { getMessageText } from "../selectors/message";

export const register = createAsyncThunk(
  "auth/register",
  async (userData, thunkAPI) => {
    try {
      const response = await AuthService.register(userData);

      // چک کردن isSuccess
      if (!response.isSuccess) {
        const message = response.message || "خطایی رخ داده است";
        thunkAPI.dispatch(setMessage(message));
        return thunkAPI.rejectWithValue(message);
      }

      const user = normalizeAuthUser(response.data);
      if (!user?.accessToken) {
        const message =
          response.message || "ثبت‌نام انجام شد اما ورود خودکار ممکن نشد.";
        thunkAPI.dispatch(setMessage(message));
        return thunkAPI.rejectWithValue(message);
      }

      return { user };
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async ({ username, password }, thunkAPI) => {
    try {
      const response = await AuthService.login(username, password);

      if (!response.isSuccess) {
        const message = response.message || "خطایی رخ داده است";
        thunkAPI.dispatch(setMessage(message));
        return thunkAPI.rejectWithValue(message);
      }

      return { user: normalizeAuthUser(response.data) };
    } catch (error) {
      const message = getMessageText(
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
          error.message ||
          error.toString()
      );
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const loginByCode = createAsyncThunk(
  "auth/loginByCode",
  async ({ mobileNumber, verifyCode }, thunkAPI) => {
    try {
      const res = await AuthService.checkVerifyCode({
        mobileNumber,
        verifyCode,
      });

      if (res.data) {
        return { user: normalizeAuthUser(res.data) };
      } else {
        throw res;
      }
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue();
    }
  }
);

export const logout = createAsyncThunk("auth/logout", async () => {
  await AuthService.logout();
});

export const checkVerifyCode = createAsyncThunk(
  "auth/checkVerifyCode",
  async ({ mobileNumber, verifyCode, forSignup = false }, thunkAPI) => {
    try {
      const response = await AuthService.checkVerifyCode({
        mobileNumber,
        verifyCode,
        forSignup,
      });

      if (!response.isSuccess) {
        const message = response.message || "کد تایید نامعتبر است";
        thunkAPI.dispatch(setMessage(message));
        return thunkAPI.rejectWithValue(message);
      }

      return response;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const requestVerifyCode = createAsyncThunk(
  "auth/requestVerifyCode",
  async (mobileNumber, thunkAPI) => {
    try {
      const response = await AuthService.requestVerifyCode(mobileNumber);

      if (!response.isSuccess) {
        const message =
          response.message || "خطایی در ارسال کد تایید رخ داده است";
        thunkAPI.dispatch(setMessage(message));
        return thunkAPI.rejectWithValue(message);
      }

      thunkAPI.dispatch(
        setMessage({
          text: "کد تایید به شماره موبایل شما ارسال شد",
          type: "success",
        })
      );
      return response.data;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (data, thunkAPI) => {
    try {
      const res = await AuthService.refreshToken();
      const user = normalizeAuthUser(res?.data);
      if (res?.isSuccess && user?.accessToken) {
        return { user };
      } else {
        return thunkAPI.rejectWithValue();
      }
    } catch (error) {
      return thunkAPI.rejectWithValue();
    }
  }
);

export const fetchLoginKind = createAsyncThunk(
  "auth/fetchLoginKind",
  async (_, thunkAPI) => {
    try {
      const res = await AuthService.getLoginKind();
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue();
    }
  }
);

export const impersonateUser = createAsyncThunk(
  "auth/impersonateUser",
  async (userInfoId, thunkAPI) => {
    try {
      const currentUser = readStoredUser();
      if (!currentUser?.accessToken) {
        return thunkAPI.rejectWithValue("نشست ورود شما منقضی شده است.");
      }

      const response = await UserManagementService.impersonateUserApi(userInfoId);
      if (!response?.isSuccess) {
        const message = response?.message || "ورود به حساب کاربر ناموفق بود.";
        thunkAPI.dispatch(setMessage(message));
        return thunkAPI.rejectWithValue(message);
      }

      const nextUser = normalizeAuthUser(response.data);
      if (!nextUser?.accessToken) {
        return thunkAPI.rejectWithValue("پاسخ سرور نامعتبر است.");
      }

      if (!saveImpersonationOriginal(currentUser)) {
        return thunkAPI.rejectWithValue("ذخیره حساب اصلی ناموفق بود.");
      }

      return { user: nextUser };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "ورود به حساب کاربر ناموفق بود.";
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const stopImpersonation = createAsyncThunk(
  "auth/stopImpersonation",
  async (_, thunkAPI) => {
    const originalUser = readImpersonationOriginal();
    if (!originalUser?.accessToken) {
      return thunkAPI.rejectWithValue("حساب اصلی یافت نشد.");
    }

    clearImpersonationOriginal();
    return { user: originalUser };
  }
);

export const fetchSystemConfig = createAsyncThunk(
  "auth/fetchSystemConfig",
  async (_, thunkAPI) => {
    try {
      const res = await DefaultApiService.getApi(
        "SystemConfig/GetSetting",
        {},
        true
      );
      if (res.data) {
        localStorage.setItem("system-config", JSON.stringify(res.data));
      }
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue();
    }
  }
);
